'use client';

import { type MotionValue } from 'motion/react';
import { useEffect, useRef } from 'react';

type OnSeek = (video: HTMLVideoElement, progress: number) => void;

// 스크롤 진행률에 맞춰 영상 재생 위치를 옮긴다(프레임당 1회).
// 재생 위치가 정해질 때마다 onSeek 를 호출해 호출부가 파생 상태를 계산하게 한다.
export function useVideoScrub(
  progress: MotionValue<number>,
  enabled: boolean,
  onSeek?: OnSeek,
) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const onSeekRef = useRef(onSeek);
  useEffect(() => {
    onSeekRef.current = onSeek;
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !enabled) return;
    let frame: number | undefined;
    const hasDuration = () =>
      Number.isFinite(video.duration) && video.duration > 0;
    const notify = () => {
      if (hasDuration()) onSeekRef.current?.(video, progress.get());
    };
    const seek = () => {
      frame = undefined;
      if (!hasDuration()) return;
      // 앞선 탐색이 끝나기 전에 새 위치를 넣으면 브라우저가 그 탐색을 버리고 다시 시작한다.
      // 탐색이 한 프레임보다 오래 걸리는 기기에서는 매번 취소만 반복돼 화면에 프레임이
      // 거의 그려지지 않는다.
      if (video.seeking) return;
      const position = Math.max(0, Math.min(1, progress.get()));
      video.currentTime = position * video.duration;
      notify();
    };
    const scheduleSeek = () => {
      if (frame === undefined) frame = requestAnimationFrame(seek);
    };
    const handleSeeked = () => {
      notify();
      scheduleSeek();
    };
    const unsubscribe = progress.on('change', scheduleSeek);
    video.addEventListener('loadedmetadata', scheduleSeek);
    video.addEventListener('seeked', handleSeeked);
    scheduleSeek();
    return () => {
      unsubscribe();
      video.removeEventListener('loadedmetadata', scheduleSeek);
      video.removeEventListener('seeked', handleSeeked);
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [progress, enabled]);

  return videoRef;
}
